import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Boxes,
  Search,
  Building2,
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Crown,
  Star,
  Zap,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ModuleCard, ModuleConfigModal } from '../../components/modules';
import {
  useTenantModules,
  useEnableModule,
  useDisableModule,
  useUpdateModuleConfig,
  useUpdateTenantTier,
} from '../../hooks/useModules';
import {
  PlatformModule,
  PlanTier,
  TenantModuleStatus,
  TIER_LABELS,
} from '../../types/modules';
import api from '../../lib/api';

interface Tenant {
  id: string;
  name: string;
  cnpj: string;
  tier?: PlanTier;
}

export function PlatformModules() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [configModule, setConfigModule] = useState<TenantModuleStatus | null>(null);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);

  // Fetch tenants
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await api.get('/super-admin/tenants');
        setTenants(response.data);
        if (response.data.length > 0 && !selectedTenantId) {
          setSelectedTenantId(response.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching tenants:', error);
      } finally {
        setIsLoadingTenants(false);
      }
    };
    fetchTenants();
  }, []);

  // Hooks
  const { data: tenantModules, isLoading, refetch } = useTenantModules(selectedTenantId);
  const enableModule = useEnableModule();
  const disableModule = useDisableModule();
  const updateConfig = useUpdateModuleConfig();
  const updateTier = useUpdateTenantTier();

  // Get unique categories
  const categories = tenantModules?.modules
    ? [...new Set(tenantModules.modules.map(m => m.category))]
    : [];

  // Filter modules
  const filteredModules = tenantModules?.modules.filter(module => {
    const matchesSearch = module.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      module.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || module.category === categoryFilter;
    return matchesSearch && matchesCategory;
  }) || [];

  // Group by category
  const modulesByCategory = filteredModules.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {} as Record<string, TenantModuleStatus[]>);

  // Handlers
  const handleToggleModule = async (moduleId: string, enabled: boolean) => {
    if (!selectedTenantId) return;

    try {
      if (enabled) {
        await enableModule.mutateAsync({
          tenantId: selectedTenantId,
          moduleId: moduleId as PlatformModule,
        });
      } else {
        await disableModule.mutateAsync({
          tenantId: selectedTenantId,
          moduleId: moduleId as PlatformModule,
        });
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao alterar módulo');
    }
  };

  const handleSaveConfig = async (moduleId: string, config: Record<string, any>) => {
    if (!selectedTenantId) return;

    await updateConfig.mutateAsync({
      tenantId: selectedTenantId,
      moduleId: moduleId as PlatformModule,
      config,
    });
  };

  const handleUpdateTier = async (tier: PlanTier) => {
    if (!selectedTenantId) return;

    try {
      await updateTier.mutateAsync({
        tenantId: selectedTenantId,
        tier,
      });
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar tier');
    }
  };

  // Stats
  const enabledCount = tenantModules?.modules.filter(m => m.isEnabled).length || 0;
  const totalCount = tenantModules?.modules.length || 0;

  const getTierIcon = (tier: PlanTier) => {
    switch (tier) {
      case PlanTier.BASIC:
        return <Star className="w-4 h-4" />;
      case PlanTier.STANDARD:
        return <Zap className="w-4 h-4" />;
      case PlanTier.PREMIUM:
        return <Crown className="w-4 h-4" />;
    }
  };

  const getTierColor = (tier: PlanTier) => {
    switch (tier) {
      case PlanTier.BASIC:
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case PlanTier.STANDARD:
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case PlanTier.PREMIUM:
        return 'bg-amber-100 text-amber-700 border-amber-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Boxes className="w-7 h-7 text-blue-600" />
            Módulos da Plataforma
          </h1>
          <p className="text-gray-500 mt-1">
            Gerencie os módulos disponíveis para cada tenant
          </p>
        </div>
        <Button
          onClick={() => refetch()}
          variant="secondary"
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {/* Tenant Selector */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Building2 className="w-4 h-4 inline mr-1" />
              Selecionar Tenant
            </label>
            <Select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              disabled={isLoadingTenants}
            >
              <option value="">Selecione um tenant...</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} ({tenant.cnpj})
                </option>
              ))}
            </Select>
          </div>

          {tenantModules && (
            <div className="flex items-end gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plano do Tenant
                </label>
                <div className="flex gap-2">
                  {Object.values(PlanTier).map((tier) => (
                    <button
                      key={tier}
                      onClick={() => handleUpdateTier(tier)}
                      className={`
                        flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all
                        ${tenantModules.tier === tier
                          ? getTierColor(tier) + ' ring-2 ring-offset-1'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }
                      `}
                    >
                      {getTierIcon(tier)}
                      {TIER_LABELS[tier]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Stats */}
      {tenantModules && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Módulos Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{enabledCount}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Boxes className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total de Módulos</p>
                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${getTierColor(tenantModules.tier)}`}>
                {getTierIcon(tenantModules.tier)}
              </div>
              <div>
                <p className="text-sm text-gray-500">Plano Atual</p>
                <p className="text-2xl font-bold text-gray-900">
                  {TIER_LABELS[tenantModules.tier]}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar módulos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="w-48">
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">Todas as categorias</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {/* Modules Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : !selectedTenantId ? (
        <Card className="p-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Selecione um Tenant
          </h3>
          <p className="text-gray-500">
            Escolha um tenant acima para gerenciar seus módulos
          </p>
        </Card>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredModules.map((module) => (
              <ModuleCard
                key={module.id}
                module={module}
                tenantTier={tenantModules?.tier || PlanTier.BASIC}
                onToggle={handleToggleModule}
                onConfigure={setConfigModule}
                isLoading={enableModule.isPending || disableModule.isPending}
                canManage={true}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Config Modal */}
      <ModuleConfigModal
        module={configModule}
        isOpen={!!configModule}
        onClose={() => setConfigModule(null)}
        onSave={handleSaveConfig}
        isLoading={updateConfig.isPending}
      />
    </div>
  );
}

export default PlatformModules;
